/**
 * Turtle — green turtles working the reef and the lagoon. Common, unhurried, everywhere.
 *
 * A turtle does not paddle, it FLIES, and the whole animation rests on that: thrust comes
 * from the blade twisting a quarter cycle out of phase with the sweep (see TurtleMaterial).
 * This file owns what the shader cannot — which behaviour it is in, and where the body goes.
 *
 * LOCOMOTION IS EARNED, NOT SCRIPTED
 * Thrust is applied on the DOWNSTROKE ONLY, from the same phase the shader is posing, and
 * drag is low. That pairing is the entire turtle read: a couple of strong beats and a long
 * coast, with the animal visibly accelerating on the beat and not between them. Push it along
 * at constant speed and no amount of flipper animation will save it.
 */
import * as THREE from 'three';
import { Rng } from '../core/Rng.ts';
import { OCEAN } from '../data/tuning.ts';
import { TurtleMaterial } from '../render/TurtleMaterial.ts';
import { bedHeight, waterDepth, lagoonEdge, COAST } from '../world/Coast.ts';
import { buildTurtle } from '../world/turtle/voxelTurtle.js';
import type { SkyEnv } from '../world/DayNight.ts';

/** the generator's own voxel pitch, in MODEL units */
const VOX = 0.028;
/** model nose-to-tail, measured from the build */
const MODEL_LEN = 1.76;
/* A quarter to a half of the shark, which lands at ~8.1 u. Sizes vary per individual: a
   turtle population is all ages at once, and a uniform one reads as a spawner. */
const SIZE_RANGE: [number, number] = [2.0, 4.05];

/** how far along the coast one may drift from the player before it is recycled */
const WINDOW_X = 165;

interface Pose { sweep: number; twist: number; rate: number; fold: number; pitch: number }
const BEH: Record<string, Pose> = {
  cruise: { sweep: 0.62, twist: 0.60, rate: 1.00, fold: 0, pitch: 0.00 },
  glide: { sweep: 0.10, twist: 0.16, rate: 0.22, fold: 0, pitch: -0.04 },
  climb: { sweep: 0.80, twist: 0.72, rate: 1.45, fold: 0, pitch: 0.42 },
  rest: { sweep: 0.03, twist: 0.05, rate: 0.10, fold: 1, pitch: 0.00 },
};
/* Weighted to the long slow states. A turtle that beat its flippers continuously would be a
   turtle in a hurry, which is the one thing they never are. */
const ORDER = ['cruise', 'glide', 'cruise', 'glide', 'climb', 'glide', 'rest', 'cruise'];
const DWELL: Record<string, [number, number]> = {
  cruise: [7, 14], glide: [8, 18], climb: [5, 9], rest: [9, 20],
};

/** cross-shore homes. Green turtles graze seagrass in the lagoon and rest on the reef. */
const HOMES: Array<[number, number]> = [[22, 52], [56, 104], [40, 78]];

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

interface Individual {
  root: THREE.Group;
  mesh: THREE.InstancedMesh;
  material: TurtleMaterial;
  scale: number;
  p: THREE.Vector3;
  v: THREE.Vector3;
  heading: number;
  beh: string;
  behIdx: number;
  dwell: number;
  live: Pose & { bank: number };
  stroke: number;
  band: [number, number];
  /** fraction of the local water column it prefers */
  column: number;
  wanderA: number;
  wanderB: number;
  t: number;
}

export class Turtle {
  readonly group = new THREE.Group();

  private readonly individuals: Individual[] = [];
  private readonly rng: Rng;
  private readonly geometry: THREE.BufferGeometry;
  private readonly voxels: number;

  constructor(count = 5, seed = 0x7c17e) {
    this.rng = new Rng(seed >>> 0);
    this.group.name = 'turtles';

    const D = buildTurtle({ vox: VOX, seed: this.rng.int(1, 1 << 28), span: 1, dome: 1, belly: 1, taper: 1 });
    this.voxels = D.n;

    this.geometry = new THREE.BoxGeometry(1, 1, 1);
    const rest = new Float32Array(D.n * 3);
    const col = new Float32Array(D.n * 3);
    for (let i = 0; i < D.n; i++) {
      rest[i * 3] = D.x[i];
      rest[i * 3 + 1] = D.y[i];
      rest[i * 3 + 2] = D.z[i];
      // The generator authors pigment in display space; this pipeline shades in LINEAR with
      // ACES on the end, so the values have to be converted or every turtle comes out chalky.
      col[i * 3] = toLinear(D.cr[i]);
      col[i * 3 + 1] = toLinear(D.cg[i]);
      col[i * 3 + 2] = toLinear(D.cbl[i]);
    }
    this.geometry.setAttribute('aRest', new THREE.InstancedBufferAttribute(rest, 3));
    this.geometry.setAttribute('aCol', new THREE.InstancedBufferAttribute(col, 3));
    this.geometry.setAttribute('aFlip', new THREE.InstancedBufferAttribute(D.fl, 1));
    this.geometry.setAttribute('aS', new THREE.InstancedBufferAttribute(D.fs, 1));
    this.geometry.setAttribute('aC', new THREE.InstancedBufferAttribute(D.fc, 1));
    this.geometry.setAttribute('aT', new THREE.InstancedBufferAttribute(D.ft, 1));
    this.geometry.setAttribute('aRole', new THREE.InstancedBufferAttribute(D.role, 1));
    this.geometry.setAttribute('aShade', new THREE.InstancedBufferAttribute(D.shade, 1));

    const identity = new THREE.Matrix4();
    for (let i = 0; i < count; i++) {
      /* Size is a per-instance SCALE on one shared geometry rather than a rebuild. Six
         separate builds would be six geometries and six times the memory for a difference
         the eye reads purely as size. */
      const scale = this.rng.range(SIZE_RANGE[0], SIZE_RANGE[1]) / MODEL_LEN;
      const material = new TurtleMaterial(VOX, scale);
      material.uniforms.uFLen.value = D.foreLen;
      material.uniforms.uRLen.value = D.rearLen;
      material.uniforms.uSeam.value = this.rng.range(0.42, 0.70);
      material.uniforms.uStreak.value = this.rng.range(0.42, 0.82);
      // Carapace tone, dark olive through umber to honey.
      const h = this.rng.next();
      (material.uniforms.uShellA.value as THREE.Color).setRGB(
        lerp(0.150, 0.300, h), lerp(0.145, 0.215, h), lerp(0.105, 0.110, h));
      (material.uniforms.uShellB.value as THREE.Color).setRGB(
        lerp(0.400, 0.680, h), lerp(0.330, 0.500, h), lerp(0.160, 0.215, h));

      const mesh = new THREE.InstancedMesh(this.geometry, material, D.n);
      for (let k = 0; k < D.n; k++) mesh.setMatrixAt(k, identity);
      mesh.instanceMatrix.needsUpdate = true;
      mesh.frustumCulled = false;

      const root = new THREE.Group();
      root.scale.setScalar(scale);
      root.rotation.order = 'YXZ';
      root.add(mesh);
      this.group.add(root);

      const band = HOMES[this.rng.int(0, HOMES.length - 1)];
      const a: Individual = {
        root, mesh, material, scale,
        p: new THREE.Vector3(), v: new THREE.Vector3(),
        heading: this.rng.range(0, Math.PI * 2),
        beh: 'cruise', behIdx: 0, dwell: this.rng.range(4, 12),
        live: { sweep: 0.62, twist: 0.60, rate: 1, fold: 0, pitch: 0, bank: 0 },
        stroke: this.rng.range(0, Math.PI * 2),
        band,
        column: this.rng.range(0.20, 0.70),
        wanderA: this.rng.range(0.10, 0.19),
        wanderB: this.rng.range(0.23, 0.39),
        t: this.rng.range(0, 60),
      };
      this.individuals.push(a);
      this.place(a, 0, true);
    }
  }

  get voxelCount(): number {
    return this.voxels;
  }

  get count(): number {
    return this.individuals.length;
  }

  update(dt: number, time: number, playerX: number): void {
    for (const a of this.individuals) {
      a.t += dt;
      this.behave(a, dt);
      this.locomote(a, dt);
      this.pose(a, dt, time);
      if (Math.abs(a.p.x - playerX) > WINDOW_X) this.place(a, playerX, false);
    }
  }

  private place(a: Individual, playerX: number, initial: boolean): void {
    const off = initial
      ? this.rng.range(-WINDOW_X, WINDOW_X)
      : -Math.sign(a.p.x - playerX) * (WINDOW_X - 22);
    // Recycled inside the bay: a spot chosen blind from the player would land on a headland.
    a.p.x = Math.max(-COAST.lagoonHalf + 40, Math.min(COAST.lagoonHalf - 40, playerX + off));
    a.p.z = this.rng.range(a.band[0], a.band[1]);
    if (waterDepth(a.p.x, a.p.z) < COAST.minSwimDepth) a.p.z += 14;
    a.p.y = this.columnY(a);
    a.v.set(0, 0, 0);
    a.heading = initial ? this.rng.range(0, Math.PI * 2) : (off < 0 ? Math.PI / 2 : -Math.PI / 2);
  }

  private columnY(a: Individual): number {
    // Shell clearance scales with the animal: a big turtle needs more room under it.
    const floor = bedHeight(a.p.x, a.p.z) + 0.6 * a.scale;
    const room = Math.max(0, OCEAN.surfaceHeight - 2.0 - floor);
    return floor + room * a.column;
  }

  private behave(a: Individual, dt: number): void {
    a.dwell -= dt;
    if (a.dwell <= 0) {
      a.behIdx = (a.behIdx + 1) % ORDER.length;
      a.beh = ORDER[a.behIdx];
      const d = DWELL[a.beh];
      a.dwell = this.rng.range(d[0], d[1]);
      if (a.beh === 'cruise') a.column = this.rng.range(0.15, 0.75);
    }
  }

  private locomote(a: Individual, dt: number): void {
    const gy = bedHeight(a.p.x, a.p.z);

    // --- steer: wander, hold the band, feel the bottom -----------------------------------
    let wantX = Math.sin(a.heading);
    let wantZ = Math.cos(a.heading);
    if (a.p.z < a.band[0]) wantZ += Math.min(1.3, (a.band[0] - a.p.z) * 0.09);
    else if (a.p.z > a.band[1]) wantZ -= Math.min(1.3, (a.p.z - a.band[1]) * 0.09);
    const shallow = COAST.minSwimDepth + 1.5 - waterDepth(a.p.x, a.p.z);
    if (shallow > 0) wantZ += shallow * 0.7;
    if (a.p.z > COAST.edgeZ - 10) wantZ -= (a.p.z - (COAST.edgeZ - 10)) * 0.3;
    /* The bay has ends. Steering off them by the same soft-pull mechanism as the cross-shore
       band means an animal curves away from a headland rather than pressing into it — and it
       keeps the population in the water the player is actually in. */
    const edge = lagoonEdge(a.p.x);
    if (edge > 0.02) wantX -= Math.sign(a.p.x) * edge * 2.2;
    const wob = Math.sin(a.t * a.wanderA * Math.PI * 2) * 0.6
              + Math.sin(a.t * a.wanderB * Math.PI * 2 + 1.1) * 0.3;

    let delta = Math.atan2(wantX, wantZ) + wob * 0.35 - a.heading;
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    // Turtles turn slowly, and turn by ROLLING first — the bank follows the turn achieved.
    const step = Math.max(-0.55 * dt, Math.min(0.55 * dt, delta));
    a.heading += step;
    const turnFrac = dt > 1e-5 ? step / dt / 0.55 : 0;
    a.live.bank += (turnFrac - a.live.bank) * (1 - Math.exp(-1.6 * dt));

    if (a.beh === 'rest') {
      a.v.multiplyScalar(1 - Math.min(1, dt * 2.2));
      a.v.y += (gy + 0.6 * a.scale - a.p.y) * 2.0 * dt;
    } else {
      /* Thrust on the downstroke only — sin(phase) negative is the power half — read from the
         SAME phase the shader is posing with. That is what ties the visible beat to the
         visible acceleration; drive the two from separate clocks and the animal swims a
         fraction of a beat out of step with itself, which reads as slipping. */
      const dn = Math.max(0, -Math.sin(a.stroke));
      const power = Math.pow(dn, 1.6) * a.live.sweep * a.live.twist * 5.2 * a.scale;
      a.v.x += Math.sin(a.heading) * power * dt;
      a.v.z += Math.cos(a.heading) * power * dt;
      a.v.y += a.live.pitch * power * 0.9 * dt;
      // Near-neutral buoyancy with a soft preference for its chosen depth. A one-way sink
      // with nothing opposing it puts every turtle on the seabed within a minute.
      a.v.y += ((this.columnY(a) - a.p.y) * 0.30 - a.v.y * 0.35) * dt;
      if (a.beh === 'climb') a.v.y += 0.42 * dt * a.scale;
    }

    // Low drag is what gives a turtle its long coast between beats.
    a.v.multiplyScalar(Math.exp(-(a.beh === 'rest' ? 2.4 : 0.62) * dt));
    a.p.addScaledVector(a.v, dt);

    const floorY = gy + 0.55 * a.scale;
    if (a.p.y < floorY) { a.p.y = floorY; if (a.v.y < 0) a.v.y *= -0.10; }
    const ceil = OCEAN.surfaceHeight - 1.4;
    if (a.p.y > ceil) { a.p.y = ceil; if (a.v.y > 0) a.v.y = 0; }
  }

  private pose(a: Individual, dt: number, time: number): void {
    const T = BEH[a.beh];
    const ease = (r: number): number => 1 - Math.exp(-r * dt);
    const k = ease(1.9);
    a.live.sweep = lerp(a.live.sweep, T.sweep, k);
    a.live.twist = lerp(a.live.twist, T.twist, k);
    a.live.rate = lerp(a.live.rate, T.rate, k);
    a.live.fold = lerp(a.live.fold, T.fold, ease(1.4));
    a.live.pitch = lerp(a.live.pitch, T.pitch, k);

    a.stroke += dt * a.live.rate * 2.4;

    const u = a.material.uniforms;
    u.uTime.value = time;
    u.uStroke.value = a.stroke;
    u.uSweep.value = a.live.sweep;
    u.uTwist.value = a.live.twist;
    u.uFold.value = a.live.fold;
    u.uBank.value = a.live.bank;

    // Pitch from the actual climb rate, so it noses up when it is genuinely going up.
    const sp = Math.hypot(a.v.x, a.v.z);
    const pitch = Math.max(-0.35, Math.min(0.35, -a.v.y / Math.max(sp, 0.8) * 0.6));
    a.root.position.copy(a.p);
    a.root.rotation.set(pitch, a.heading, -a.live.bank * 0.45, 'YXZ');
  }

  /** feed the wake: position and a body-scaled radius, so a big animal parts a wide swathe */
  trackInto(wake: { track(k: number, x: number, y: number, z: number, r: number, t: number): void },
            base: number, now: number): void {
    for (let i = 0; i < this.individuals.length; i++) {
      const a = this.individuals[i];
      wake.track(base + i, a.p.x, a.p.y, a.p.z, 2.0 * a.scale, now);
    }
  }

  setEnv(env: SkyEnv): void {
    for (const a of this.individuals) a.material.setEnv(env);
  }

  setShadow(map: THREE.Texture | null, matrix: THREE.Matrix4, strength: number): void {
    for (const a of this.individuals) a.material.setShadow(map, matrix, strength);
  }

  debugRow(): string {
    const by: Record<string, number> = {};
    for (const a of this.individuals) by[a.beh] = (by[a.beh] ?? 0) + 1;
    return `${this.individuals.length} · ${Object.entries(by).map(([k, v]) => `${v} ${k}`).join(', ')}`;
  }

  dispose(): void {
    this.group.removeFromParent();
    this.geometry.dispose();
    for (const a of this.individuals) {
      a.mesh.dispose();
      a.material.dispose();
    }
    this.individuals.length = 0;
  }
}

function toLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
